import React, { Component } from 'react';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import {
  openPaymentConfiguration as openPaymentConfigurationClevertap,
  addPaymentConfiguration as addPaymentConfigurationClevertap,
} from 'clevertap/paymentConfiguration';
import withFeatureFlags from 'hocs/withFeatureFlags';
import styles from './PaymentConfiguration.css';
import PaymentConfigurationList from './PaymentConfigurationList';
import PaymentConfigurationForm from './PaymentConfigurationForm';
import UnsavedChangeModal from './UnsavedChangeModal';
import PaymentConfigurationFtue from './PaymentConfigurationFtue';

class PaymentConfiguration extends Component {
  constructor(props) {
    super(props);

    this.state = {
      searchTerm: '',
      hasChange: false,
      showUnsavedChangeModal: false,
    };

    this.actionAfterUnsavedChange = () => {};
  }

  componentDidMount() {
    const { onLoad, router, route } = this.props;
    onLoad();

    // Use `setRouteLeaveHook` to prevent transition to other pages
    // Doc: https://github.com/ReactTraining/react-router/blob/v3/docs/guides/ConfirmingNavigation.md
    //
    // This function returns an `unregister` function to remove itself from
    // listening to navigation events. We use this to prevent infinite confirmation
    // loop. (Has change -> Prompt -> Proceed -> Still has change -> Prompt -> ...)
    const unregister = router.setRouteLeaveHook(route, nextLocation => {
      this.withUnsavedChangeCheck(() => {
        unregister();
        router.push(nextLocation);
      });
      return false;
    });

    openPaymentConfigurationClevertap();
  }

  componentWillUnmount() {
    const { onUnload } = this.props;
    onUnload();
  }

  /**
   * A convenient function to check if there is currently any unsaved changes.
   * If there is, prompt for confirmation.
   */
  withUnsavedChangeCheck = (cb = () => {}) => {
    const { hasChange } = this.state;

    if (hasChange) {
      this.actionAfterUnsavedChange = cb;
      this.setState({
        showUnsavedChangeModal: true,
      });
    } else {
      cb();
    }
  };

  handleListItemClick = itemId =>
    this.withUnsavedChangeCheck(() => {
      const {
        selectedConfigurationId,
        selectConfiguration,
        deselectConfiguration,
      } = this.props;

      if (itemId === selectedConfigurationId) {
        deselectConfiguration();
      } else {
        selectConfiguration(itemId);
      }
    });

  handleFormChange = changes => {
    this.setState({
      hasChange: Object.keys(changes).length > 0,
    });
  };

  handleFormClose = () =>
    this.withUnsavedChangeCheck(() => {
      const { stopCreation, deselectConfiguration } = this.props;
      stopCreation();
      deselectConfiguration();
    });

  handleAddButtonClick = () => {
    addPaymentConfigurationClevertap();
    this.withUnsavedChangeCheck(() => {
      const { startCreation } = this.props;
      startCreation();
    });
  };

  handleSearchTermChange = event => {
    this.setState({
      searchTerm: event.target.value,
    });
  };

  handleUnsavedChangeModalCancel = () => {
    this.setState({
      showUnsavedChangeModal: false,
    });
  };

  handleUnsavedChangeModalConfirm = () => {
    this.setState(
      {
        hasChange: false,
        showUnsavedChangeModal: false,
      },
      this.actionAfterUnsavedChange
    );
  };

  handleFtueFormOpenRequest = () => {
    const { startCreation } = this.props;
    startCreation();
  };

  handleFtueFormCloseRequest = () => {
    const { stopCreation } = this.props;
    stopCreation();
  };

  render() {
    const {
      configurations,
      showFtue,
      onTourComplete,
      selectedConfigurationId,
      isCreatingNew,
      showPayconAndroidComing,
    } = this.props;
    const { searchTerm, showUnsavedChangeModal } = this.state;

    const showDetails = isCreatingNew || selectedConfigurationId !== null;

    const filteredConfigurations = configurations.filter(({ name }) =>
      name.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );

    return (
      <div className={styles.container}>
        <h2>Payment Configuration</h2>

        <div className={styles.actionBar}>
          <input
            type="text"
            placeholder="Search"
            className={classNames('form-control', styles.searchBox)}
            value={searchTerm}
            onChange={this.handleSearchTermChange}
          />
          <div className={styles.actionBarRight}>
            <div className={classNames('alert alert-warning', styles.alertBox)}>
              <i
                className={classNames(
                  'glyphicon glyphicon-info-sign',
                  styles.infoIcon
                )}
              />
              <div>
                This feature requires at least Moka v26.4 on IOS &amp; v16.1 on
                Android
                {showPayconAndroidComing && (
                  <div>(Android coming soon at the mid of July)</div>
                )}
              </div>
            </div>

            <button
              className="btn btn-primary paycon-ftue-add-button"
              onClick={this.handleAddButtonClick}
              disabled={isCreatingNew}
            >
              Add Configuration
            </button>
          </div>
        </div>

        <div className={styles.mainContent}>
          <div className={styles.list}>
            <PaymentConfigurationList
              selectedId={selectedConfigurationId}
              configurations={filteredConfigurations}
              onItemClick={this.handleListItemClick}
            />
          </div>

          <CSSTransition
            in={showDetails}
            timeout={200}
            mountOnEnter
            unmountOnExit
            classNames={{
              enterDone: styles.detailsEnterDone,
            }}
          >
            <div className={styles.details}>
              {/*
                `key` is useful to clear state :P
                See: https://kentcdodds.com/blog/understanding-reacts-key-prop
              */}
              <PaymentConfigurationForm
                key={selectedConfigurationId}
                configurationId={selectedConfigurationId}
                isNew={isCreatingNew}
                onChange={this.handleFormChange}
                onClose={this.handleFormClose}
              />
            </div>
          </CSSTransition>
        </div>

        {showUnsavedChangeModal && (
          <UnsavedChangeModal
            onCancel={this.handleUnsavedChangeModalCancel}
            onConfirm={this.handleUnsavedChangeModalConfirm}
          />
        )}

        {showFtue && (
          <PaymentConfigurationFtue
            isFormOpen={showDetails}
            onRequestFormOpen={this.handleFtueFormOpenRequest}
            onRequestFormClose={this.handleFtueFormCloseRequest}
            onFormTourComplete={onTourComplete}
          />
        )}
      </div>
    );
  }
}

PaymentConfiguration.defaultProps = {
  showFtue: false,
};

const mapFlagsToProps = flags => ({
  showPayconAndroidComing: flags.showPayconAndroidComing,
});

export default withFeatureFlags(mapFlagsToProps)(PaymentConfiguration);
